import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { CancelAppointmentSkeleton } from "@/components/cancel-appointment/CancelAppointmentSkeleton";

const PREDEFINED_REASONS = [
  { id: "schedule_conflict", label: "Conflit d'horaire" },
  { id: "feeling_ill", label: "Je me sens malade" },
  { id: "transportation_issue", label: "Problème de transport" },
  { id: "changed_mind", label: "J'ai changé d'avis sur le service" },
  { id: "other", label: "Autre raison" },
];

const CancelAppointment = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [appointment, setAppointment] = useState<any | null>(null);
  const [confirmCancellation, setConfirmCancellation] = useState(false);
  const [cancellationSuccess, setCancellationSuccess] = useState(false);

  const [selectedReasonType, setSelectedReasonType] = useState<string>("");
  const [customReason, setCustomReason] = useState<string>("");
  const [showCustomReasonField, setShowCustomReasonField] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const token = searchParams.get("token");

        if (!token) {
          throw new Error("Lien invalide: token manquant");
        }

        // Récupère le rendez-vous via la fonction sécurisée (token-gated)
        const { data: appointmentData, error: rpcError } = await supabase.rpc(
          "get_appointment_by_token",
          { p_token: token, p_action: "client_cancel" }
        );

        if (rpcError || !appointmentData) {
          throw new Error("Lien invalide ou expiré");
        }

        const apt: any = appointmentData;
        if (apt.error === "used") {
          throw new Error("Ce lien a déjà été utilisé pour annuler le rendez-vous");
        }
        if (apt.error === "expired") {
          throw new Error("Ce lien a expiré");
        }

        if (apt.status === "cancelled") {
          throw new Error("Ce rendez-vous a déjà été annulé");
        }

        setAppointment(apt);
      } catch (error: any) {
        console.error("Erreur lors de la vérification du token:", error);
        setErrorMessage(error.message || "Une erreur s'est produite");
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [searchParams]);

  const handleReasonTypeChange = (value: string) => {
    setSelectedReasonType(value);
    setShowCustomReasonField(value === "other");
  };

  const handleSubmitCancellation = async () => {
    if (!appointment) return;

    if (!selectedReasonType) {
      alert("Veuillez sélectionner une raison d'annulation");
      return;
    }

    if (selectedReasonType === "other" && !customReason.trim()) {
      alert("Veuillez préciser la raison de votre annulation");
      return;
    }

    try {
      setSubmitting(true);
      const token = searchParams.get("token");

      const reasonText =
        selectedReasonType === "other"
          ? customReason.trim()
          : PREDEFINED_REASONS.find((r) => r.id === selectedReasonType)?.label || selectedReasonType;

      // Annulation sécurisée et atomique via la fonction SECURITY DEFINER
      const { data, error: rpcError } = await supabase.rpc("cancel_appointment_by_token", {
        p_token: token,
        p_reason_type: selectedReasonType,
        p_reason: reasonText,
      });

      if (rpcError) throw new Error("Une erreur s'est produite lors de l'annulation");
      const result: any = data;
      if (!result || result.error) {
        if (result?.error === "used") throw new Error("Ce lien a déjà été utilisé");
        if (result?.error === "expired") throw new Error("Ce lien a expiré");
        throw new Error("Lien invalide ou expiré");
      }

      const services =
        (result.appointment_services || []).map((item: any) => ({
          name: item.services.name,
          price: item.services.price,
        })) || [];

      // Envoyer notification à l'administrateur
      await supabase.functions.invoke("send-email", {
        body: {
          type: "appointment_cancellation",
          data: {
            appointment_id: appointment.id,
            client_name: appointment.client_name,
            client_email: appointment.client_email,
            client_phone: appointment.client_phone,
            date: appointment.date,
            start_time: appointment.start_time.substring(0, 5),
            services,
            cancellation_reason: reasonText,
          },
        },
      });

      // Envoyer email de confirmation au client
      await supabase.functions.invoke("send-email", {
        body: {
          type: "booking_user_status",
          data: {
            client_name: appointment.client_name,
            client_email: appointment.client_email,
            date: appointment.date,
            start_time: appointment.start_time.substring(0, 5),
            end_time: appointment.end_time.substring(0, 5),
            services,
            status: "cancelled",
            cancellation_reason: reasonText,
            cancelled_by: "client",
          },
        },
      });

      // Afficher l'écran de succès
      setCancellationSuccess(true);
    } catch (error: any) {
      console.error("Erreur lors de l'annulation:", error);
      setErrorMessage(error.message || "Une erreur s'est produite lors de l'annulation");
      setSubmitting(false);
    }
  };

  // Formatage des données pour l'affichage
  const formattedDate = appointment?.date ? format(new Date(appointment.date), "EEEE d MMMM yyyy", { locale: fr }) : "";

  const formatTime = (time: string) => time?.substring(0, 5) || "";

  if (loading) {
    return <CancelAppointmentSkeleton />;
  }

  if (errorMessage) {
    return (
      <div className="pt-24 pb-16 px-4">
        <div className="container mx-auto max-w-md">
          <Card>
            <CardHeader>
              <CardTitle className="text-center text-destructive">Erreur</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="mb-6">{errorMessage}</p>
              <Button onClick={() => navigate("/")}>Retour à l'accueil</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (cancellationSuccess) {
    return (
      <div className="pt-24 pb-16 px-4">
        <div className="container mx-auto max-w-lg">
          <Card>
            <CardHeader>
              <div className="flex justify-center mb-4">
                <CheckCircle2 className="h-16 w-16 text-bbz-green" />
              </div>
              <CardTitle className="text-center">Votre rendez-vous a bien été annulé</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-6 p-4 bg-muted/20 rounded-md">
                <h3 className="font-medium mb-3">Récapitulatif du rendez-vous annulé:</h3>
                <p className="capitalize text-sm mb-2">{formattedDate}</p>
                <p className="text-sm mb-2">
                  Horaire: {formatTime(appointment?.start_time)} - {formatTime(appointment?.end_time)}
                </p>
                <div className="mt-3">
                  <p className="font-medium text-sm mb-1">Services:</p>
                  <ul className="ml-4 list-disc text-sm space-y-1">
                    {appointment?.appointment_services?.map((item: any, idx: number) => (
                      <li key={idx}>{item.services.name}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="bg-bbz-cream/30 p-4 rounded-md mb-6">
                <p className="text-sm text-center text-muted-foreground">
                  Un email de confirmation vous a été envoyé à l'adresse <strong>{appointment?.client_email}</strong>
                </p>
              </div>

              <div className="flex justify-center">
                <Button onClick={() => navigate("/")} className="bg-bbz-green hover:bg-bbz-green/90">
                  Retour à l'accueil
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 px-4">
      <div className="container mx-auto max-w-lg">
        <Card>
          <CardHeader>
            <CardTitle>Annulation de rendez-vous</CardTitle>
            <CardDescription>
              {confirmCancellation
                ? "Merci de nous indiquer la raison de votre annulation"
                : "Veuillez confirmer l'annulation de votre rendez-vous"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!confirmCancellation ? (
              <>
                <div className="mb-6 p-4 bg-bbz-cream/30 rounded-md">
                  <h3 className="font-medium mb-2">Détails du rendez-vous:</h3>
                  <p className="capitalize">{formattedDate}</p>
                  <p>
                    Horaire: {formatTime(appointment?.start_time)} - {formatTime(appointment?.end_time)}
                  </p>
                  <div className="mt-2">
                    <p className="font-medium">Services réservés:</p>
                    <ul className="ml-4 list-disc">
                      {appointment?.appointment_services?.map((item: any, idx: number) => (
                        <li key={idx}>
                          {item.services.name} - {item.services.price}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  <p className="text-bbz-text/70 text-sm">Êtes-vous sûr de vouloir annuler ce rendez-vous?</p>
                  <div className="flex gap-3 justify-center">
                    <Button variant="outline" onClick={() => navigate("/")}>
                      Retour
                    </Button>
                    <Button variant="destructive" onClick={() => setConfirmCancellation(true)}>
                      Confirmer l'annulation
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-6">
                  <div>
                    <h3 className="font-medium mb-3">Raison de votre annulation:</h3>
                    <RadioGroup value={selectedReasonType} onValueChange={handleReasonTypeChange}>
                      {PREDEFINED_REASONS.map((reason) => (
                        <div key={reason.id} className="flex items-center space-x-2 my-2">
                          <RadioGroupItem value={reason.id} id={reason.id} />
                          <Label htmlFor={reason.id} className="cursor-pointer">
                            {reason.label}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>

                  {showCustomReasonField && (
                    <div className="space-y-2">
                      <Label htmlFor="customReason">Précisez votre raison</Label>
                      <Textarea
                        id="customReason"
                        placeholder="Veuillez préciser la raison de votre annulation"
                        value={customReason}
                        onChange={(e) => setCustomReason(e.target.value)}
                      />
                    </div>
                  )}

                  <div className="flex justify-center gap-3 pt-4">
                    <Button variant="outline" onClick={() => setConfirmCancellation(false)} disabled={submitting}>
                      Retour
                    </Button>
                    <Button
                      onClick={handleSubmitCancellation}
                      disabled={
                        submitting || !selectedReasonType || (selectedReasonType === "other" && !customReason.trim())
                      }
                      className="bg-bbz-green hover:bg-bbz-green/90"
                    >
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Envoyer et annuler
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CancelAppointment;
